import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import AvisoUltimaNota from '../components/AvisoUltimaNota'
import NovoClienteModal from '../components/NovoClienteModal'
import CidadeSelect from '../components/CidadeSelect'
import ItemSelect from '../components/ItemSelect'
import NaturezaSelect from '../components/NaturezaSelect'
import IbsCbsSelect from '../components/IbsCbsSelect'
import FichaViagemModal from '../components/FichaViagemModal'

// Base legal obrigatória nas Informações Complementares quando há INSS retido
// (art. 117, I, da IN RFB nº 2.110/2022) — ver useEffect que adiciona/remove
// essa linha sozinho conforme o INSS calculado.
const TEXTO_LEGAL_INSS = 'INSS RETIDO: 11% sobre base de cálculo de 30% do valor do serviço, conforme art. 117, I, da IN RFB nº 2.110/2022.'

interface IbsCbs {
  cst: string; classTrib: string; classTribDes: string
  cbsBc: number; cbsBcEf: number; cbsAliRef: number; cbsRedAli: number; cbsAliEf: number; cbsValor: number
  ibsUfEst: string; ibsUfBcEf: number; ibsUfAliRef: number; ibsUfRedAli: number; ibsUfAliEf: number; ibsUfValor: number
  ibsMunBcEf: number; ibsMunAliRef: number; ibsMunRedAli: number; ibsMunAliEf: number; ibsMunValor: number
  isSbas: number; isSali: number
}

const ibsCbsVazio: IbsCbs = {
  cst: '', classTrib: '', classTribDes: '',
  cbsBc: 0, cbsBcEf: 0, cbsAliRef: 0, cbsRedAli: 0, cbsAliEf: 0, cbsValor: 0,
  ibsUfEst: '', ibsUfBcEf: 0, ibsUfAliRef: 0, ibsUfRedAli: 0, ibsUfAliEf: 0, ibsUfValor: 0,
  ibsMunBcEf: 0, ibsMunAliRef: 0, ibsMunRedAli: 0, ibsMunAliEf: 0, ibsMunValor: 0,
  isSbas: 0, isSali: 0,
}

function calcIbsCbs(total: number, r: any): IbsCbs {
  if (!r || total <= 0) return { ...ibsCbsVazio }
  const rr = (v: number) => Math.round(v * 100) / 100
  const bcEfC = rr(total * (1 - Number(r.ibs08_crbc) / 100))
  const bcEfU = rr(total * (1 - Number(r.ibs08_urbc) / 100))
  const bcEfM = rr(total * (1 - Number(r.ibs08_mrbc) / 100))
  return {
    cst: r.ibs08_cst?.trim() ?? '', classTrib: r.ibs08_tri?.trim() ?? '', classTribDes: r.ibs08_d02?.trim() ?? '',
    cbsBc: total, cbsBcEf: bcEfC,
    cbsAliRef: Number(r.ibs08_cali), cbsRedAli: Number(r.ibs08_cpra), cbsAliEf: Number(r.ibs08_caef), cbsValor: rr(bcEfC * Number(r.ibs08_caef) / 100),
    ibsUfEst: r.ibs08_uest?.trim() ?? '', ibsUfBcEf: bcEfU,
    ibsUfAliRef: Number(r.ibs08_uali), ibsUfRedAli: Number(r.ibs08_upra), ibsUfAliEf: Number(r.ibs08_uaef), ibsUfValor: rr(bcEfU * Number(r.ibs08_uaef) / 100),
    ibsMunBcEf: bcEfM,
    ibsMunAliRef: Number(r.ibs08_mali), ibsMunRedAli: Number(r.ibs08_mpra), ibsMunAliEf: Number(r.ibs08_maef), ibsMunValor: rr(bcEfM * Number(r.ibs08_maef) / 100),
    isSbas: Number(r.ibs08_sbas), isSali: Number(r.ibs08_sali),
  }
}

interface Natureza {
  tno_cod: number; tno_cdf: number; tno_des: string; tno_dsc: string
  tno_tis: string; tno_bis: number; tno_ais: number; tno_ris: string
  tno_tin: string; tno_bin: number; tno_ain: number; tno_rin: string
  tno_tir: string; tno_bir: number; tno_air: number; tno_rir: string
  tno_tpi: string; tno_bpi: number; tno_api: number; tno_rip: string
  tno_tcf: string; tno_bcf: number; tno_acf: number
  tno_tcs: string; tno_bcs: number; tno_acs: number
  tno_cidco1?: number; cidade_des?: string; cid_codmun?: number; cidade_uf?: string
  tno_nat?: string; tno_loc?: string
}

function r2(v: number) { return Math.round(v * 100) / 100 }

function calcImpostosInfisc(total: number, nat: Natureza | undefined) {
  if (!nat || total <= 0) return null
  const bc = (base: number, p: number) => r2(base * Number(p) / 100)
  const issBase  = bc(total, nat.tno_bis)
  const inssBase = bc(total, nat.tno_bin)
  const irBase   = bc(total, nat.tno_bir)
  const pisBase  = bc(total, nat.tno_bpi)
  const cofBase  = bc(total, nat.tno_bcf)
  const csllBase = bc(total, nat.tno_bcs)
  // CSLL não tem flag de retenção própria (sempre retido). COFINS não tem flag própria
  // no cadastro da natureza — acompanha o mesmo flag do PIS (tno_rip), já que na prática
  // PIS/COFINS são retidos ou não como par.
  return {
    vBCISS:       issBase  > 0 ? issBase.toFixed(2)  : '',
    pISS:         nat.tno_ais > 0 ? String(nat.tno_ais) : '',
    issRetido:    nat.tno_ris?.trim() === 'S',
    vBCINSS:      inssBase > 0 ? inssBase.toFixed(2) : '',
    pRetINSS:     nat.tno_ain > 0 ? String(nat.tno_ain) : '',
    inssRetido:   nat.tno_rin?.trim() === 'S',
    vBCRetIR:     irBase   > 0 ? irBase.toFixed(2)   : '',
    pRetIR:       nat.tno_air > 0 ? String(nat.tno_air) : '',
    irRetido:     nat.tno_rir?.trim() === 'S',
    vBCCOFINS:    cofBase  > 0 ? cofBase.toFixed(2)  : '',
    pRetCOFINS:   nat.tno_acf > 0 ? String(nat.tno_acf) : '',
    vBCCSLL:      csllBase > 0 ? csllBase.toFixed(2) : '',
    pRetCSLL:     nat.tno_acs > 0 ? String(nat.tno_acs) : '',
    vBCPISPASEP:  pisBase  > 0 ? pisBase.toFixed(2)  : '',
    pRetPISPASEP: nat.tno_api > 0 ? String(nat.tno_api) : '',
    pisRetido:    nat.tno_rip?.trim() === 'S',
  }
}

interface Cliente {
  cli_cod:    string
  cli_mat:    string
  cli_nom:    string
  cli_fan:    string
  cli_fj:     string
  cli_est:    string
  bloqueado?: boolean
  end_logradouro?: string
  end_numero?:     string
  end_bairro?:     string
  end_cidade?:     string
  end_uf?:         string
}

interface Empresa {
  empcod: number
  empnom: string
  empfan: string
  empcgc: string
  empest: string
  empsigla: string
}

interface Endereco {
  logradouro: string
  numero:     string
  bairro:     string
  cidade:     string
  uf:         string
  cep:        string
  ibge?:      string
}

interface Resultado {
  ok:          boolean
  nNFSe?:      string
  chaveAcesso?: string
  xMotivo?:    string
  erro?:       string
  serie?:      string
  numero?:     number
}

function fmtDoc(doc: string) {
  const d = doc.replace(/\D/g, '')
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  return doc
}

function hoje() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

// Só remove separador de milhar (.) quando há vírgula decimal (formato BR, ex:
// "2.569,53"); sem vírgula, assume que já está em formato simples (ex: "5.00"
// vindo de cálculo/cadastro) e não toca no ponto, senão "5.00" virava 500.
const n = (s: string) => {
  const str = String(s ?? '').trim()
  if (str.includes(',')) return Number(str.replace(/\./g, '').replace(',', '.')) || 0
  return Number(str) || 0
}

// Nota existente (edição/cópia) com mais de um item: monta a lista a partir de `servicos` do backend.
// Retenção (ISS/INSS/IR/PIS) é da Natureza, igual pra todos os itens; o cálculo do IBS/CBS vem da regra de cada item.
async function itensDoServidor(d: any): Promise<ItemServ[]> {
  return Promise.all((d.servicos as any[]).map(async (s: any) => {
    const base = {
      itsCod: s.itsCod ?? '', itsDes: s.itsDes ?? '', itsUn: s.itsUn ?? 'UN',
      cServ: s.cServ ?? '', cLCServ: s.cLCServ ?? '', xServ: s.xServ ?? '', uTrib: s.uTrib || 'UN',
      qTrib: s.qTrib || '1.00', vUnit: s.vUnit ?? '', vServ: s.vServ ?? '', cNBS: s.cNBS ?? '',
      pISS: s.pISS ?? '', vBCISS: s.vBCISS ?? '', issRetido: !!d.issRetido,
      pRetINSS: s.pRetINSS ?? '', vBCINSS: s.vBCINSS ?? '', inssRetido: !!d.inssRetido,
      pRetIR: s.pRetIR ?? '', vBCRetIR: s.vBCRetIR ?? '', irRetido: !!d.irRetido,
      pRetCOFINS: s.pRetCOFINS ?? '', vBCCOFINS: s.vBCCOFINS ?? '',
      pRetCSLL: s.pRetCSLL ?? '', vBCCSLL: s.vBCCSLL ?? '',
      pRetPISPASEP: s.pRetPISPASEP ?? '', vBCPISPASEP: s.vBCPISPASEP ?? '', pisRetido: !!d.pisRetido,
      ibsCbsRegra: s.ibsCbsCod ?? '', ibsCbsDes: s.ibsCbsDes ?? '',
    }
    let ibsCbs: IbsCbs = { ...ibsCbsVazio }
    if (base.ibsCbsRegra) {
      try {
        const r = await api.get(`/erp/ibscbs/${base.ibsCbsRegra}`)
        const bc = baseIbsCbs(base)
        if (r.data && bc > 0) ibsCbs = calcIbsCbs(bc, r.data)
      } catch { /* sem o cálculo do IBS/CBS a regra continua selecionada — recalcula ao editar o item */ }
    }
    return { ...base, ibsCbs, ...valoresItem(base) }
  }))
}

// Item (serviço) da nota: o formulário edita um por vez e os já adicionados ficam numa lista
interface ItemServ {
  itsCod: string; itsDes: string; itsUn: string
  cServ: string; cLCServ: string; xServ: string; uTrib: string; qTrib: string; vUnit: string; vServ: string; cNBS: string
  pISS: string; vBCISS: string; issRetido: boolean
  pRetINSS: string; vBCINSS: string; inssRetido: boolean
  pRetIR: string; vBCRetIR: string; irRetido: boolean
  pRetCOFINS: string; vBCCOFINS: string
  pRetCSLL: string; vBCCSLL: string
  pRetPISPASEP: string; vBCPISPASEP: string; pisRetido: boolean
  ibsCbsRegra: string; ibsCbsDes: string; ibsCbs: IbsCbs
  vISS: string; vINSS: string; vIR: string; vCOFINS: string; vCSLL: string; vPIS: string // base × alíquota
}

const valorTrib = (bc: string, p: string) => { const b = n(bc), a = n(p); return b > 0 && a > 0 ? (b * a / 100).toFixed(2) : '0.00' }

function valoresItem(it: Pick<ItemServ, 'vServ' | 'vBCISS' | 'pISS' | 'vBCINSS' | 'pRetINSS' | 'vBCRetIR' | 'pRetIR' | 'vBCCOFINS' | 'pRetCOFINS' | 'vBCCSLL' | 'pRetCSLL' | 'vBCPISPASEP' | 'pRetPISPASEP'>) {
  return {
    vISS:    valorTrib(it.vBCISS || it.vServ, it.pISS),
    vINSS:   valorTrib(it.vBCINSS, it.pRetINSS),
    vIR:     valorTrib(it.vBCRetIR, it.pRetIR),
    vCOFINS: valorTrib(it.vBCCOFINS || it.vServ, it.pRetCOFINS),
    vCSLL:   valorTrib(it.vBCCSLL, it.pRetCSLL),
    vPIS:    valorTrib(it.vBCPISPASEP || it.vServ, it.pRetPISPASEP),
  }
}

// ERP deduz do pagamento: ISS (quando retido) + INSS + IR + CSLL sempre; COFINS só quando retido
// (acompanha o flag do PIS); PIS não é deduzido do líquido
const retencaoItem = (it: ItemServ) =>
  (it.issRetido ? n(it.vISS) : 0) + (it.inssRetido ? n(it.vINSS) : 0) + (it.irRetido ? n(it.vIR) : 0) + (it.pisRetido ? n(it.vCOFINS) : 0) + n(it.vCSLL)

// Base do IBS/CBS exclui PIS, COFINS e ISSQN (impostos sobre imposto)
function baseIbsCbs(it: Pick<ItemServ, 'vServ' | 'vBCISS' | 'pISS' | 'vBCCOFINS' | 'pRetCOFINS' | 'vBCPISPASEP' | 'pRetPISPASEP'>) {
  const iss = n(valorTrib(it.vBCISS || it.vServ, it.pISS))
  const cof = n(valorTrib(it.vBCCOFINS || it.vServ, it.pRetCOFINS))
  const pis = n(valorTrib(it.vBCPISPASEP || it.vServ, it.pRetPISPASEP))
  return Math.max(n(it.vServ) - iss - cof - pis, 0)
}

export default function EmitirNFSeInfisc() {
  const [etapa, setEtapa] = useState(0)

  // Empresa emitente
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresa,  setEmpresa]  = useState<Empresa | null>(null)
  const { empresaCod: empresaCodGlobal } = useEmpresa()

  // Tomador
  const [busca, setBusca]     = useState('')
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [buscando, setBuscando] = useState(false)
  const [cliente, setCliente]   = useState<Cliente | null>(null)
  const [endereco, setEndereco] = useState<Endereco | null>(null)
  const [modalNovo, setModalNovo] = useState(false)
  const [clifroNat, setClifroNat] = useState<number | undefined>(undefined)

  // Endereço do tomador
  const [endTomLgr,    setEndTomLgr]    = useState('')
  const [endTomNro,    setEndTomNro]    = useState('SN')
  const [endTomCpl,    setEndTomCpl]    = useState('')
  const [endTomBairro, setEndTomBairro] = useState('')
  const [endTomCEP,    setEndTomCEP]    = useState('')
  const [endTomCMun,   setEndTomCMun]   = useState('')
  const [endTomXMun,   setEndTomXMun]   = useState('')
  const [endTomUF,     setEndTomUF]     = useState('RS')

  // CFOP / Natureza
  const [naturezas,  setNaturezas]  = useState<Natureza[]>([])
  const [natureza,   setNatureza]   = useState('')

  // Dados fiscais
  const [numero,     setNumero]     = useState('')
  const [serie,      setSerie]      = useState('')
  const [numeroViagem, setNumeroViagem] = useState('')
  const [modalFichaViagem, setModalFichaViagem] = useState(false)
  const [dCompet,    setDCompet]    = useState(hoje)
  const [cLocPrest,  setCLocPrest]  = useState('')     // IBGE
  const [cLocXMun,   setCLocXMun]   = useState('')
  const [cLocCod,    setCLocCod]    = useState('')
  const [cLocUf,     setCLocUf]     = useState('')

  // Item (itscad)
  const [itsCod,     setItsCod]     = useState('')
  const [itsDes,     setItsDes]     = useState('')
  const [itsUn,      setItsUn]      = useState('UN')

  const [cServ,      setCServ]      = useState('')
  const [cLCServ,    setCLCServ]    = useState('')
  const [xServ,      setXServ]      = useState('')
  const [uTrib,      setUTrib]      = useState('UN')
  const [qTrib,      setQTrib]      = useState('1.00')
  const [vUnit,      setVUnit]      = useState('')
  const [vServ,      setVServ]      = useState('')
  const [cNBS,       setCNBS]       = useState('')

  // Tributos — ISS e retenções
  const [pISS,         setPISS]         = useState('')
  const [vBCISS,       setVBCISS]       = useState('')
  const [issRetido,    setIssRetido]    = useState(false)
  const [pRetINSS,     setPRetINSS]     = useState('')
  const [vBCINSS,      setVBCINSS]      = useState('')
  const [inssRetido,   setInssRetido]   = useState(false)
  const [pRetIR,       setPRetIR]       = useState('')
  const [vBCRetIR,     setVBCRetIR]     = useState('')
  const [irRetido,     setIrRetido]     = useState(false)
  const [pRetCOFINS,   setPRetCOFINS]   = useState('')
  const [vBCCOFINS,    setVBCCOFINS]    = useState('')
  const [pRetCSLL,     setPRetCSLL]     = useState('')
  const [vBCCSLL,      setVBCCSLL]      = useState('')
  const [pRetPISPASEP, setPRetPISPASEP] = useState('')
  const [vBCPISPASEP,  setVBCPISPASEP]  = useState('')
  const [pisRetido,    setPisRetido]    = useState(false)

  // IBS/CBS (reforma tributária)
  const [ibsCbsRegra, setIbsCbsRegra] = useState('')
  const [ibsCbsDes,   setIbsCbsDes]   = useState('')
  const [ibsCbsMunIbge, setIbsCbsMunIbge] = useState('')
  const [ibsCbs, setIbsCbs] = useState<IbsCbs>({ ...ibsCbsVazio })
  const [abaFiscal, setAbaFiscal] = useState<'impostos' | 'ibscbs'>('impostos')

  const [infAdics, setInfAdics] = useState('')

  // Itens já adicionados à nota (o formulário acima é o item em digitação)
  const [itens, setItens] = useState<ItemServ[]>([])
  const [itemEditIdx, setItemEditIdx] = useState<number | null>(null)
  const pularRecalcRef = useRef(false) // item carregado no formulário não pode ser recalculado pela Natureza

  // Financeiro
  const [financeiroModal, setFinanceiroModal]       = useState(false)
  const [finQtdParcelas,  setFinQtdParcelas]        = useState(1)
  const [finIntervalo,    setFinIntervalo]           = useState(0)
  const [finCondDes,      setFinCondDes]              = useState('')
  const [finParcelas,     setFinParcelas]            = useState<{ numero: string; vencimento: string; valor: string }[]>([])
  const [finContaCod,     setFinContaCod]            = useState('')
  const [finContaNome,    setFinContaNome]           = useState('')
  const [finContaResultados, setFinContaResultados] = useState<{ dep_cod: string; dep_des: string }[]>([])
  const [finContaAberto,  setFinContaAberto]         = useState(false)

  const [erroEtapa1, setErroEtapa1] = useState<string | null>(null)
  const [enviando,   setEnviando]   = useState(false)
  const [resultado,  setResultado]  = useState<Resultado | null>(null)

  const buscarTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [editId, setEditId] = useState<number | null>(() => {
    const v = new URLSearchParams(window.location.search).get('editId')
    return v ? Number(v) : null
  })
  const [editEmpcod, setEditEmpcod] = useState<number | null>(() => {
    const v = new URLSearchParams(window.location.search).get('empcod')
    return v ? Number(v) : null
  })
  const [copyId] = useState<number | null>(() => {
    const v = new URLSearchParams(window.location.search).get('copyId')
    return v ? Number(v) : null
  })
  const [copyEmpcod] = useState<number | null>(() => {
    const v = new URLSearchParams(window.location.search).get('empcod')
    return v ? Number(v) : null
  })
  const [carregandoEdicao, setCarregandoEdicao] = useState(false)
  const [erroEdicao, setErroEdicao] = useState<string | null>(null)

  // Lista de empresas emitentes
  useEffect(() => {
    api.get('/erp/empresas').then(r => {
      setEmpresas(r.data)
      if (r.data.length > 0 && !empresa) setEmpresa(r.data.find((e: Empresa) => e.empcod === empresaCodGlobal) ?? r.data[0])
    }).catch(() => {})
  }, [])

  // Trocou a empresa no topo → emitente acompanha (exceto edição/cópia, que usam a empresa da nota)
  useEffect(() => {
    if (editEmpcod !== null || copyEmpcod !== null || !empresaCodGlobal) return
    const emp = empresas.find(e => e.empcod === empresaCodGlobal)
    if (emp) setEmpresa(emp)
  }, [empresaCodGlobal, empresas])

  // Seleciona a empresa correta ao carregar em modo edição
  useEffect(() => {
    if (editEmpcod !== null && empresas.length > 0) {
      const emp = empresas.find(e => e.empcod === editEmpcod)
      if (emp) setEmpresa(emp)
    }
  }, [editEmpcod, empresas])

  // Carrega dados para edição quando editId está presente
  useEffect(() => {
    const id = searchParams.get('editId')
    if (!id) return
    const numId = Number(id)
    setEditId(numId)
    setEtapa(1)          // vai para etapa 1 imediatamente, antes da API
    setCarregandoEdicao(true)
    api.get(`/nfse-infisc/${numId}/dados?empcod=${editEmpcod ?? 1}`)
      .then(r => {
        const d = r.data
        setItens([]); setItemEditIdx(null); limparForm() // não herda itens de uma nota aberta antes
        if (d.numero)        setNumero(String(d.numero))
        if (d.numeroViagem)  setNumeroViagem(String(d.numeroViagem))
        if (d.tomador) {
          setCliente(d.tomador); setBusca(d.tomador.cli_nom ?? '')
          // Busca o endereço fresco do cadastro (inclui o IBGE certo) em vez de só
          // confiar no <ender> do XML antigo — numa nota rejeitada por cidade errada
          // (Error5000/E0240), o XML tem exatamente o valor errado que rejeitou.
          api.get(`/erp/clientes/${d.tomador.cli_cod}/endereco`)
            .then(r => setEndereco(r.data?.logradouro ? r.data : null))
            .catch(() => {})
        }
        if (d.endTomLgr)     setEndTomLgr(d.endTomLgr)
        if (d.endTomNro)     setEndTomNro(d.endTomNro)
        if (d.endTomCpl)     setEndTomCpl(d.endTomCpl)
        if (d.endTomBairro)  setEndTomBairro(d.endTomBairro)
        if (d.endTomCEP)     setEndTomCEP(d.endTomCEP)
        if (d.endTomCMun)    setEndTomCMun(d.endTomCMun)
        if (d.endTomXMun)    setEndTomXMun(d.endTomXMun)
        if (d.endTomUF)      setEndTomUF(d.endTomUF)
        if (d.cLocPrest)     setCLocPrest(d.cLocPrest)
        if (d.cLocXMun)      setCLocXMun(d.cLocXMun)
        if (d.cLocCod)       setCLocCod(d.cLocCod)
        if (d.cLocUf)        setCLocUf(d.cLocUf)
        if (d.naturezaCod)   setNatureza(d.naturezaCod)
        if (d.ibsCbsCod)     { setIbsCbsRegra(d.ibsCbsCod); setIbsCbsDes(d.ibsCbsDes ?? '') }
        if (d.itsCod)        { setItsCod(d.itsCod); setItsDes(d.itsDes ?? ''); setItsUn(d.itsUn ?? '') }
        if (d.dCompet)       setDCompet(d.dCompet)
        if (d.cServ)         setCServ(d.cServ)
        if (d.cLCServ)       setCLCServ(d.cLCServ)
        if (d.xServ)         setXServ(d.xServ)
        if (d.uTrib)         setUTrib(d.uTrib)
        if (d.qTrib)         setQTrib(d.qTrib)
        if (d.vUnit)         setVUnit(d.vUnit)
        if (d.vServ)         setVServ(d.vServ)
        if (d.cNBS)          setCNBS(d.cNBS)
        if (d.vBCISS)        setVBCISS(d.vBCISS)
        if (d.pISS)          setPISS(d.pISS)
        if (d.vBCINSS)       setVBCINSS(d.vBCINSS)
        if (d.pRetINSS)      setPRetINSS(d.pRetINSS)
        if (d.vBCRetIR)      setVBCRetIR(d.vBCRetIR)
        if (d.pRetIR)        setPRetIR(d.pRetIR)
        if (d.vBCCOFINS)     setVBCCOFINS(d.vBCCOFINS)
        if (d.pRetCOFINS)    setPRetCOFINS(d.pRetCOFINS)
        if (d.vBCCSLL)       setVBCCSLL(d.vBCCSLL)
        if (d.pRetCSLL)      setPRetCSLL(d.pRetCSLL)
        if (d.vBCPISPASEP)   setVBCPISPASEP(d.vBCPISPASEP)
        if (d.pRetPISPASEP)  setPRetPISPASEP(d.pRetPISPASEP)
        setIssRetido(!!d.issRetido);   setInssRetido(!!d.inssRetido)
        setIrRetido(!!d.irRetido);     setPisRetido(!!d.pisRetido)
        if (d.infAdics)      setInfAdics(d.infAdics)
        // Nota com mais de um item: todos vão pra lista e o formulário fica livre pro próximo
        if (Array.isArray(d.servicos) && d.servicos.length > 1) {
          itensDoServidor(d).then(lista => { setItens(lista); limparForm() })
        }
      })
      .catch(e => setErroEdicao(e?.response?.data?.error ?? 'Erro ao carregar nota para edição'))
      .finally(() => setCarregandoEdicao(false))
  }, [searchParams])

  // Seleciona a empresa da nota copiada como ponto de partida (usuário pode trocar)
  useEffect(() => {
    if (copyEmpcod !== null && empresas.length > 0 && !editId) {
      const emp = empresas.find(e => e.empcod === copyEmpcod)
      if (emp) setEmpresa(emp)
    }
  }, [copyEmpcod, empresas, editId])

  // Carrega dados de outra nota pra copiar — igual à edição, mas sem número/série
  // (ganham valor novo automaticamente) e funciona pra nota em qualquer situação
  useEffect(() => {
    if (!copyId || editId != null) return
    setEtapa(1)
    setCarregandoEdicao(true)
    api.get(`/nfse-infisc/${copyId}/dados?empcod=${copyEmpcod ?? 1}&copia=1`)
      .then(r => {
        const d = r.data
        setItens([]); setItemEditIdx(null); limparForm() // não herda itens de uma nota aberta antes
        if (d.tomador) {
          setCliente(d.tomador); setBusca(d.tomador.cli_nom ?? '')
          api.get(`/erp/clientes/${d.tomador.cli_cod}/endereco`)
            .then(r2 => setEndereco(r2.data?.logradouro ? r2.data : null))
            .catch(() => {})
        }
        if (d.endTomLgr)     setEndTomLgr(d.endTomLgr)
        if (d.endTomNro)     setEndTomNro(d.endTomNro)
        if (d.endTomCpl)     setEndTomCpl(d.endTomCpl)
        if (d.endTomBairro)  setEndTomBairro(d.endTomBairro)
        if (d.endTomCEP)     setEndTomCEP(d.endTomCEP)
        if (d.endTomCMun)    setEndTomCMun(d.endTomCMun)
        if (d.endTomXMun)    setEndTomXMun(d.endTomXMun)
        if (d.endTomUF)      setEndTomUF(d.endTomUF)
        if (d.cLocPrest)     setCLocPrest(d.cLocPrest)
        if (d.cLocXMun)      setCLocXMun(d.cLocXMun)
        if (d.cLocCod)       setCLocCod(d.cLocCod)
        if (d.cLocUf)        setCLocUf(d.cLocUf)
        if (d.naturezaCod)   setNatureza(d.naturezaCod)
        if (d.ibsCbsCod)     { setIbsCbsRegra(d.ibsCbsCod); setIbsCbsDes(d.ibsCbsDes ?? '') }
        if (d.itsCod)        { setItsCod(d.itsCod); setItsDes(d.itsDes ?? ''); setItsUn(d.itsUn ?? '') }
        if (d.cServ)         setCServ(d.cServ)
        if (d.cLCServ)       setCLCServ(d.cLCServ)
        if (d.xServ)         setXServ(d.xServ)
        if (d.uTrib)         setUTrib(d.uTrib)
        if (d.qTrib)         setQTrib(d.qTrib)
        if (d.vUnit)         setVUnit(d.vUnit)
        if (d.vServ)         setVServ(d.vServ)
        if (d.cNBS)          setCNBS(d.cNBS)
        if (d.vBCISS)        setVBCISS(d.vBCISS)
        if (d.pISS)          setPISS(d.pISS)
        if (d.vBCINSS)       setVBCINSS(d.vBCINSS)
        if (d.pRetINSS)      setPRetINSS(d.pRetINSS)
        if (d.vBCRetIR)      setVBCRetIR(d.vBCRetIR)
        if (d.pRetIR)        setPRetIR(d.pRetIR)
        if (d.vBCCOFINS)     setVBCCOFINS(d.vBCCOFINS)
        if (d.pRetCOFINS)    setPRetCOFINS(d.pRetCOFINS)
        if (d.vBCCSLL)       setVBCCSLL(d.vBCCSLL)
        if (d.pRetCSLL)      setPRetCSLL(d.pRetCSLL)
        if (d.vBCPISPASEP)   setVBCPISPASEP(d.vBCPISPASEP)
        if (d.pRetPISPASEP)  setPRetPISPASEP(d.pRetPISPASEP)
        setIssRetido(!!d.issRetido);   setInssRetido(!!d.inssRetido)
        setIrRetido(!!d.irRetido);     setPisRetido(!!d.pisRetido)
        if (d.infAdics)      setInfAdics(d.infAdics)
        // Competência: usa a data de hoje, não a da nota original
        setDCompet(new Date().toISOString().slice(0, 10))
        if (Array.isArray(d.servicos) && d.servicos.length > 1) {
          itensDoServidor(d).then(lista => { setItens(lista); limparForm() })
        }
      })
      .catch(e => setErroEdicao(e?.response?.data?.error ?? 'Erro ao carregar nota para copiar'))
      .finally(() => setCarregandoEdicao(false))
  }, [copyId, copyEmpcod, editId])

  useEffect(() => {
    if (editId != null) return // em edição, o número já vem carregado — não sobrescreve
    api.get(`/nfse-infisc/proximonumero/${empresa?.empcod ?? 1}`)
      .then(r => {
        setNumero(String(r.data.numero ?? ''))
        setSerie(String(r.data.serie ?? ''))
      })
      .catch(() => {})
  }, [empresa, editId])

  useEffect(() => {
    api.get('/erp/naturezas')
      .then(r => setNaturezas(r.data))
      .catch(() => {})
  }, [])
  // no campo só entram naturezas de saída (tno_nat 3) com modelo Municipal; a já gravada na nota continua aparecendo
  const naturezasInfisc = naturezas.filter(n =>
    (String(n.tno_nat ?? '').trim() === '3' && String(n.tno_loc ?? '').trim().toUpperCase() === 'M') || String(n.tno_cod) === natureza)

  function handleBusca(val: string) {
    setBusca(val)
    if (buscarTimer.current) clearTimeout(buscarTimer.current)
    if (val.length < 2) { setClientes([]); return }
    setBuscando(true)
    buscarTimer.current = setTimeout(async () => {
      try {
        const r = await api.get('/erp/clientes/lista', { params: { busca: val, limit: 10 } })
        setClientes(r.data.clientes ?? r.data ?? [])
      } catch { setClientes([]) }
      finally { setBuscando(false) }
    }, 350)
  }

  async function selecionarCliente(c: Cliente) {
    setCliente(c)
    setClientes([])
    setBusca('')
    api.get(`/erp/clientes/${c.cli_cod}/endereco`)
      .then(r => setEndereco(r.data?.logradouro ? r.data : null))
      .catch(() => {})
    api.get(`/erp/clientes/${c.cli_cod}/clifro`)
      .then(r => setClifroNat(r.data?.clifronat ? Number(r.data.clifronat) : undefined))
      .catch(() => setClifroNat(undefined))
    // Observação cadastrada no cliente — sugere no campo, mas é só o valor inicial:
    // editar aqui não altera o cadastro do cliente. O "VENCIMENTO:" só entra depois,
    // quando o financeiro for informado (ver Confirmar do modal Financeiro).
    const [rAdicionais, rCondPgto] = await Promise.all([
      api.get(`/erp/clientes/${c.cli_cod}/adicionais`).catch(() => null),
      api.get(`/erp/clientes/${c.cli_cod}/condicao-pagamento`).catch(() => null),
    ])
    const obsCliente = rAdicionais?.data?.vnd_cod_obs_des ?? ''
    setFinIntervalo(rCondPgto?.data?.dias ?? 0)
    setFinCondDes(rCondPgto?.data?.des ?? '')
    setInfAdics(obsCliente)
    setEtapa(1)
  }

  // Nº da Viagem informado — busca a ficha (mesma lógica do CT-e OS) e auto-preenche
  // as Informações Adicionais
  useEffect(() => {
    if (!numeroViagem) return
    let cancelado = false
    const t = setTimeout(() => {
      api.get(`/cte-os/fichas-viagem/${numeroViagem}`).then(() => {
        if (cancelado) return
        setInfAdics(prev => {
          const linha = `Ficha de viagem: ${numeroViagem}`
          const semFicha = prev.replace(/^Ficha de viagem: \d+\n?/, '')
          if (semFicha === prev && prev.includes(linha)) return prev
          return semFicha ? `${linha}\n${semFicha}` : linha
        })
      }).catch(() => {})
    }, 400)
    return () => { cancelado = true; clearTimeout(t) }
  }, [numeroViagem])

  // Auto-seleciona a CFOP/Natureza cadastrada como "municipal" no cadastro do cliente
  useEffect(() => {
    if (!clifroNat) return
    const nat = naturezas.find(n => n.tno_cod === clifroNat)
    if (nat) setNatureza(String(nat.tno_cod))
  }, [clifroNat, naturezas])

  // Se a CFOP/Natureza selecionada tem um Município Base cadastrado, o Local de Prestação
  // tem que ser essa mesma cidade — pré-preenche automaticamente. Sem natureza selecionada
  // (campo em branco) ou sem município base cadastrado, o usuário escolhe a cidade livremente.
  useEffect(() => {
    const nat = naturezas.find(n => String(n.tno_cod) === natureza)
    if (!nat?.cid_codmun) return
    setCLocCod(String(nat.tno_cidco1))
    setCLocXMun(nat.cidade_des ?? '')
    setCLocPrest(String(nat.cid_codmun))
    setCLocUf(nat.cidade_uf ?? '')
  }, [natureza, naturezas])

  useEffect(() => {
    if (endereco) {
      setEndTomLgr(endereco.logradouro ?? '')
      setEndTomNro(endereco.numero ?? 'SN')
      setEndTomBairro(endereco.bairro ?? '')
      setEndTomCEP(endereco.cep ?? '')
      setEndTomXMun(endereco.cidade ?? '')
      setEndTomUF(endereco.uf ?? 'RS')
      // IBGE real da cidade do cliente — sem isso o cMun do tomador caía no
      // fallback pro cLocPrest (local de prestação, pode ser outra cidade) e
      // o INFISC rejeitava com "cidade do tomador não confere" (Error5000).
      setEndTomCMun(endereco.ibge ?? '')
    }
  }, [endereco])

  // Recalcula impostos quando natureza ou valor do serviço muda
  useEffect(() => {
    if (pularRecalcRef.current) { pularRecalcRef.current = false; return }
    const nat = naturezas.find(nat => String(nat.tno_cod) === natureza)
    const vs  = n(vServ)
    const calc = calcImpostosInfisc(vs, nat)
    if (!calc) return
    setVBCISS(calc.vBCISS);           setPISS(calc.pISS);            setIssRetido(calc.issRetido)
    setVBCINSS(calc.vBCINSS);         setPRetINSS(calc.pRetINSS);    setInssRetido(calc.inssRetido)
    setVBCRetIR(calc.vBCRetIR);       setPRetIR(calc.pRetIR);        setIrRetido(calc.irRetido)
    setVBCCOFINS(calc.vBCCOFINS);     setPRetCOFINS(calc.pRetCOFINS)
    setVBCCSLL(calc.vBCCSLL);         setPRetCSLL(calc.pRetCSLL)
    setVBCPISPASEP(calc.vBCPISPASEP); setPRetPISPASEP(calc.pRetPISPASEP); setPisRetido(calc.pisRetido)
  }, [natureza, naturezas, vServ])

  // Com INSS retido calculado, a base legal da retenção precisa constar nas
  // Informações Complementares da nota — adiciona/remove sozinho conforme a
  // MESMA condição que decide se o INSS realmente sai retido no XML (ver
  // "pRetINSS: inssRetido ? ... : '0.00'" no payload de emitir()): a flag
  // inssRetido (natureza com tno_rin='S') E o valor calculado > 0.
  useEffect(() => {
    const bc = n(vBCINSS), p = n(pRetINSS)
    // vale o item em digitação ou qualquer item já adicionado
    const temInssRetido = (inssRetido && bc > 0 && p > 0) || itens.some(it => it.inssRetido && n(it.vBCINSS) > 0 && n(it.pRetINSS) > 0)
    setInfAdics(prev => {
      const semTexto = prev.split('\n').filter(l => l.trim() !== TEXTO_LEGAL_INSS).join('\n')
      if (!temInssRetido) return semTexto
      return semTexto ? `${semTexto}\n${TEXTO_LEGAL_INSS}` : TEXTO_LEGAL_INSS
    })
  }, [vBCINSS, pRetINSS, inssRetido, itens])

  // vUnit × qTrib → vServ
  useEffect(() => {
    const vu = n(vUnit), qt = n(qTrib)
    if (vu > 0 && qt > 0) setVServ((vu * qt).toFixed(2))
  }, [vUnit, qTrib])

  // Recalcula IBS/CBS quando regra, vServ ou PIS/COFINS/ISSQN destacados mudam. Base do
  // IBS/CBS exclui PIS, COFINS e ISSQN (impostos sobre imposto) — valor bruto menos os três.
  useEffect(() => {
    if (!ibsCbsRegra) { setIbsCbs({ ...ibsCbsVazio }); setIbsCbsMunIbge(''); return }
    api.get(`/erp/ibscbs/${ibsCbsRegra}`)
      .then(r => {
        if (!r.data) return
        const bcIss  = n(vBCISS || vServ), pIss = n(pISS)
        const issVal = bcIss > 0 && pIss > 0 ? bcIss * pIss / 100 : 0
        const bcCof  = n(vBCCOFINS || vServ), pCof = n(pRetCOFINS)
        const cofVal = bcCof > 0 && pCof > 0 ? bcCof * pCof / 100 : 0
        const bcPis  = n(vBCPISPASEP || vServ), pPis = n(pRetPISPASEP)
        const pisVal = bcPis > 0 && pPis > 0 ? bcPis * pPis / 100 : 0
        const vs = Math.max(n(vServ) - issVal - cofVal - pisVal, 0)
        if (vs > 0) setIbsCbs(calcIbsCbs(vs, r.data))
        if (r.data.mun_cod) {
          setIbsCbsMunIbge(String(r.data.mun_ibge ?? ''))
          // Regra vinculada a município — IBS/CBS é devido no local de prestação.
          // Só preenche se ainda estiver vazio; não sobrescreve escolha do usuário.
          if (!cLocCod) {
            setCLocCod(String(r.data.mun_cod))
            setCLocXMun(r.data.mun_des)
            setCLocPrest(String(r.data.mun_ibge ?? ''))
            setCLocUf(r.data.mun_uf)
          }
        } else {
          setIbsCbsMunIbge('')
        }
      })
      .catch(() => {})
  }, [ibsCbsRegra, vServ, vBCISS, pISS, vBCCOFINS, pRetCOFINS, vBCPISPASEP, pRetPISPASEP])

  const vISS    = (() => { const bc = n(vBCISS || vServ), p = n(pISS); return bc > 0 && p > 0 ? (bc * p / 100).toFixed(2) : '0.00' })()
  const vINSS   = (() => { const bc = n(vBCINSS), p = n(pRetINSS); return bc > 0 && p > 0 ? (bc * p / 100).toFixed(2) : '0.00' })()
  const vIR     = (() => { const bc = n(vBCRetIR), p = n(pRetIR); return bc > 0 && p > 0 ? (bc * p / 100).toFixed(2) : '0.00' })()
  const vCOFINS = (() => { const bc = n(vBCCOFINS || vServ), p = n(pRetCOFINS); return bc > 0 && p > 0 ? (bc * p / 100).toFixed(2) : '0.00' })()
  const vCSLL   = (() => { const bc = n(vBCCSLL), p = n(pRetCSLL); return bc > 0 && p > 0 ? (bc * p / 100).toFixed(2) : '0.00' })()
  const vPIS    = (() => { const bc = n(vBCPISPASEP || vServ), p = n(pRetPISPASEP); return bc > 0 && p > 0 ? (bc * p / 100).toFixed(2) : '0.00' })()

  // Totais da nota = soma dos itens já adicionados (a Revisão e o Financeiro usam estes)
  const totServ = itens.reduce((s, it) => s + n(it.vServ), 0)
  const totalRetencoes = itens.reduce((s, it) => s + retencaoItem(it), 0)
  const vLiq = totServ - totalRetencoes

  const formPreenchido = !!(xServ.trim() || n(vServ) || cServ.trim())

  // Item em digitação → registro da lista
  function lerForm(): ItemServ {
    return {
      itsCod, itsDes, itsUn, cServ, cLCServ, xServ, uTrib, qTrib, vUnit, vServ, cNBS,
      pISS, vBCISS, issRetido, pRetINSS, vBCINSS, inssRetido, pRetIR, vBCRetIR, irRetido,
      pRetCOFINS, vBCCOFINS, pRetCSLL, vBCCSLL, pRetPISPASEP, vBCPISPASEP, pisRetido,
      ibsCbsRegra, ibsCbsDes, ibsCbs: { ...ibsCbs },
      vISS, vINSS, vIR, vCOFINS, vCSLL, vPIS,
    }
  }

  // Zera o formulário pro próximo item. A regra IBS/CBS fica (costuma ser a mesma); o cálculo dela refaz sozinho.
  function limparForm() {
    setItsCod(''); setItsDes(''); setItsUn('UN')
    setCServ(''); setCLCServ(''); setXServ(''); setUTrib('UN')
    setQTrib('1.00'); setVUnit(''); setVServ(''); setCNBS('')
    setPISS(''); setVBCISS(''); setIssRetido(false)
    setPRetINSS(''); setVBCINSS(''); setInssRetido(false)
    setPRetIR(''); setVBCRetIR(''); setIrRetido(false)
    setPRetCOFINS(''); setVBCCOFINS('')
    setPRetCSLL(''); setVBCCSLL('')
    setPRetPISPASEP(''); setVBCPISPASEP(''); setPisRetido(false)
    setIbsCbs({ ...ibsCbsVazio })
  }

  function carregarNoForm(it: ItemServ) {
    pularRecalcRef.current = true
    setTimeout(() => { pularRecalcRef.current = false }, 150) // vServ igual ao atual não dispara o efeito
    setItsCod(it.itsCod); setItsDes(it.itsDes); setItsUn(it.itsUn)
    setCServ(it.cServ); setCLCServ(it.cLCServ); setXServ(it.xServ); setUTrib(it.uTrib)
    setQTrib(it.qTrib); setVUnit(it.vUnit); setVServ(it.vServ); setCNBS(it.cNBS)
    setPISS(it.pISS); setVBCISS(it.vBCISS); setIssRetido(it.issRetido)
    setPRetINSS(it.pRetINSS); setVBCINSS(it.vBCINSS); setInssRetido(it.inssRetido)
    setPRetIR(it.pRetIR); setVBCRetIR(it.vBCRetIR); setIrRetido(it.irRetido)
    setPRetCOFINS(it.pRetCOFINS); setVBCCOFINS(it.vBCCOFINS)
    setPRetCSLL(it.pRetCSLL); setVBCCSLL(it.vBCCSLL)
    setPRetPISPASEP(it.pRetPISPASEP); setVBCPISPASEP(it.vBCPISPASEP); setPisRetido(it.pisRetido)
    setIbsCbsRegra(it.ibsCbsRegra); setIbsCbsDes(it.ibsCbsDes); setIbsCbs({ ...it.ibsCbs })
  }

  // Campos obrigatórios do item (os da nota — local, CFOP — ficam em avancarRevisao)
  function errosItem(): string[] {
    const err: string[] = []
    if (!cServ.trim())         err.push('Código do Serviço')
    if (!cLCServ.trim())       err.push('Código da Lei Complementar (selecione o Item novamente ou cadastre o código LC 116 nesse item)')
    if (!xServ.trim())         err.push('Descrição do Serviço')
    if (!n(vServ))             err.push('Valor do Serviço')
    if (!n(pISS))              err.push('Alíquota ISS')
    if (!ibsCbsRegra.trim())   err.push('IBS/CBS (Regra)')
    return err
  }

  // Devolve a lista nova (ou null se o item tem erro) pra quem chamou poder seguir sem esperar o estado
  function confirmarItem(): ItemServ[] | null {
    const err = errosItem()
    if (err.length) { setErroEtapa1(`Preencha: ${err.join(', ')}`); return null }
    const it = lerForm()
    const nova = itemEditIdx !== null ? itens.map((x, i) => i === itemEditIdx ? it : x) : [...itens, it]
    setItens(nova)
    setItemEditIdx(null)
    limparForm()
    setErroEtapa1(null)
    return nova
  }

  function editarItem(i: number) {
    if (formPreenchido) { setErroEtapa1('Adicione ou limpe o item em digitação antes de editar outro.'); return }
    setErroEtapa1(null)
    carregarNoForm(itens[i])
    setItemEditIdx(i)
  }

  function removerItem(i: number) {
    setItens(itens.filter((_, j) => j !== i))
    if (itemEditIdx === i) { setItemEditIdx(null); limparForm() }
    else if (itemEditIdx !== null && itemEditIdx > i) setItemEditIdx(itemEditIdx - 1)
  }

  function cancelarEdicaoItem() { setItemEditIdx(null); limparForm(); setErroEtapa1(null) }

  // Parte do serviço no payload de emissão
  function payloadItem(it: ItemServ) {
    return {
      cServ:    it.cServ.trim(),
      cLCServ:  it.cLCServ.trim(),
      xServ:    it.xServ.trim(),
      uTrib:    it.uTrib,
      qTrib:    it.qTrib,
      vUnit:    it.vUnit || n(it.vServ).toFixed(2),
      vServ:    n(it.vServ).toFixed(2),
      cNBS:     it.cNBS.trim() || undefined,
      itsCod:   it.itsCod ? Number(it.itsCod) : undefined,
      ibsCbsCod: it.ibsCbsRegra ? Number(it.ibsCbsRegra) : undefined,
      // ISS
      issRetido: it.issRetido,
      vBCISS:   n(it.vBCISS || it.vServ).toFixed(2),
      pISS:     n(it.pISS).toFixed(2),
      vISS:     it.vISS,
      // INSS
      vBCINSS:  n(it.vBCINSS).toFixed(2),
      pRetINSS: it.inssRetido ? n(it.pRetINSS).toFixed(2) : '0.00',
      vRetINSS: it.inssRetido ? it.vINSS : '0.00',
      // IR
      vBCRetIR: n(it.vBCRetIR).toFixed(2),
      pRetIR:   it.irRetido ? n(it.pRetIR).toFixed(2) : '0.00',
      vRetIR:   it.irRetido ? it.vIR : '0.00',
      // COFINS — sempre envia valor calculado no XML (ERP inclui mesmo sem retenção explícita)
      vBCCOFINS:  n(it.vBCCOFINS || it.vServ).toFixed(2),
      pRetCOFINS: n(it.pRetCOFINS).toFixed(2),
      vRetCOFINS: it.vCOFINS,
      // CSLL — nunca retida
      vBCCSLL:  n(it.vBCCSLL).toFixed(2),
      pRetCSLL: '0.00',
      vRetCSLL: '0.00',
      // PIS/PASEP — sempre envia valor calculado no XML (não deduz do vtLiq, mas vai no Ret)
      vBCPISPASEP:  n(it.vBCPISPASEP || it.vServ).toFixed(2),
      pRetPISPASEP: n(it.pRetPISPASEP).toFixed(2),
      vRetPISPASEP: it.vPIS,
      CSTPisCofins:   n(it.pRetPISPASEP) > 0 ? '01' : '07',
      tpRetPisCofins: n(it.pRetPISPASEP) > 0 ? '2'  : '1',
      ibsCbs: it.ibsCbs,
    }
  }

  // Cidade divergente da que está cadastrada na CFOP e/ou na regra IBS/CBS selecionada.
  // Campo em branco na CFOP/regra (sem cidade cadastrada) não restringe nada.
  const natMunBase = naturezas.find(nn => String(nn.tno_cod) === natureza)
  const cfopDivergente   = !!(natMunBase?.cid_codmun && cLocPrest && String(natMunBase.cid_codmun) !== cLocPrest)
  const ibsCbsDivergente = !!(ibsCbsMunIbge && cLocPrest && ibsCbsMunIbge !== cLocPrest)

  function avancarRevisao() {
    const err: string[] = []
    if (!cLocPrest.trim())     err.push('Local de Prestação (IBGE)')
    // Item em digitação (ou em edição) entra na lista; sem nada digitado vale a lista que já existe
    if (formPreenchido || itemEditIdx !== null || itens.length === 0) err.push(...errosItem())
    if (cfopDivergente)
      err.push(`Local de Prestação tem que ser ${natMunBase?.cidade_des} — cidade cadastrada na CFOP "${natMunBase?.tno_des}"`)
    if (ibsCbsDivergente)
      err.push(`Local de Prestação tem que ser a mesma cidade cadastrada na regra IBS/CBS "${ibsCbsDes}"`)
    if (err.length) { setErroEtapa1(`Preencha: ${err.join(', ')}`); return }
    if (formPreenchido || itemEditIdx !== null) confirmarItem()
    setErroEtapa1(null)
    setEtapa(2)
  }

  function adicionarItem() {
    if (confirmarItem()) setErroEtapa1(null)
  }

  async function emitir() {
    if (!cliente) return
    if (cliente.bloqueado) { setResultado({ ok: false, erro: 'Cliente bloqueado — não é possível emitir documentos para este cliente.' }); return }
    if (!itens.length) { setResultado({ ok: false, erro: 'Adicione ao menos um serviço à nota.' }); return }
    setEnviando(true)
    setResultado(null)
    try {
      const docToma = cliente.cli_mat.replace(/\D/g, '')
      const natSelecionada = naturezas.find(nat => String(nat.tno_cod) === natureza)
      const r = await api.post('/nfse-infisc/emitir', {
        empcod: empresa?.empcod ?? 1,
        numero: editId ?? (numero ? Number(numero) : undefined),
        isEdit: editId != null,
        dCompet,
        cLocPrestacao: cLocPrest,
        cMunCod: cLocCod ? Number(cLocCod) : undefined,
        naturezaCod: natSelecionada?.tno_cod,
        naturezaCdf: natSelecionada?.tno_cdf,
        ibsCbsCod: itens[0]?.ibsCbsRegra ? Number(itens[0].ibsCbsRegra) : undefined,
        itsCod: itens[0]?.itsCod ? Number(itens[0].itsCod) : undefined,
        numeroViagem: numeroViagem ? Number(numeroViagem) : undefined,
        // Tomador
        cnpjToma:    cliente.cli_fj === 'J' ? docToma : undefined,
        cpfToma:     cliente.cli_fj !== 'J' ? docToma : undefined,
        xNomeToma:   cliente.cli_nom || cliente.cli_fan,
        endTomLgr, endTomNro, endTomCpl, endTomBairro, endTomCEP,
        endTomCMun:  endTomCMun || cLocPrest,
        endTomXMun:  endTomXMun || cLocXMun,
        endTomUF,
        // Serviços (um por item da nota)
        servicos: itens.map(payloadItem),
        // IBS/CBS do 1º item (cabeçalho da nota)
        ibsCbs: itens[0]?.ibsCbs,
        // Infos adicionais
        infAdic: infAdics.split('\n').map(s => s.trim()).filter(Boolean),
      })
      setResultado(r.data)
      if (r.data.ok && r.data.numero && finParcelas.length > 0 && finContaCod.trim()) {
        try {
          await api.post(`/nfse-infisc/${r.data.numero}/financeiro?empcod=${empresa?.empcod ?? 1}`, {
            parcelas: finParcelas, contaCod: finContaCod, retencaoTotal: totalRetencoes,
          })
        } catch (ef: any) { console.warn('[Financeiro]', ef?.response?.data?.error || ef?.message) }
      }
      // Emitiu/autorizou de verdade — vai direto pra Lista/Transmissão, igual o
      // usuário espera (antes ficava parado na tela de Emitir sem avisar nada).
      if (r.data.ok) navigate('/nfse-infisc/lista')
    } catch (err: any) {
      setResultado({ ok: false, erro: err.response?.data?.error ?? err.message })
    } finally {
      setEnviando(false)
    }
  }

  function nova() {
    setEtapa(0); setCliente(null); setEndereco(null); setClifroNat(undefined)
    setEndTomLgr(''); setEndTomNro('SN'); setEndTomCpl(''); setEndTomBairro('')
    setEndTomCEP(''); setEndTomCMun(''); setEndTomXMun(''); setEndTomUF('RS')
    setCLocPrest(''); setCLocXMun(''); setCLocCod(''); setCLocUf('')
    setNatureza('')
    setItsCod(''); setItsDes(''); setItsUn('UN')
    setCServ(''); setCLCServ(''); setXServ(''); setUTrib('UN')
    setQTrib('1.00'); setVUnit(''); setVServ(''); setCNBS('')
    setPISS(''); setVBCISS('')
    setPRetINSS(''); setVBCINSS('')
    setPRetIR(''); setVBCRetIR('')
    setPRetCOFINS(''); setVBCCOFINS('')
    setPRetCSLL(''); setVBCCSLL('')
    setPRetPISPASEP(''); setVBCPISPASEP('')
    setIbsCbsRegra(''); setIbsCbsDes(''); setIbsCbs({ ...ibsCbsVazio }); setAbaFiscal('impostos')
    setItens([]); setItemEditIdx(null)
    setInfAdics('')
    setFinParcelas([]); setFinContaCod(''); setFinContaNome(''); setFinQtdParcelas(1); setFinIntervalo(0); setFinCondDes('')
    setResultado(null); setBusca(''); setClientes([])
    api.get(`/nfse-infisc/proximonumero/${empresa?.empcod ?? 1}`)
      .then(r => {
        setNumero(String(r.data.numero ?? ''))
        setSerie(String(r.data.serie ?? ''))
      })
      .catch(() => {})
  }

  const brl = (v: number | string) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  const secStyle: React.CSSProperties = { background: '#f8faff', border: '1.5px solid #e0e7ff', borderRadius: '0.5rem', padding: '0.65rem 1rem', marginBottom: '1rem' }
  const secLabel: React.CSSProperties = { fontSize: '0.72rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }

  return (
    <>
      {!resultado && editId == null && <AvisoUltimaNota empcod={empresa?.empcod ?? 1} modelo="99" serie={serie} proximo={numero} onNumeroAjustado={n => setNumero(String(n))} />}
      {!resultado && (
        <div className="stepper" style={{ maxWidth: 800 }}>
          {['Tomador', 'Serviço / Impostos', 'Revisão'].map((l, i) => (
            <div key={i}
              className={`step${etapa === i ? ' step--active' : i < etapa ? ' step--done' : ''}`}
              onClick={() => { if (i < etapa || (i === 1 && !!cliente)) setEtapa(i) }}
              style={{ cursor: (i < etapa || (i === 1 && !!cliente)) ? 'pointer' : 'default' }}>
              <div className="step-circle">{i < etapa ? '✓' : i + 1}</div>
              <span>{l}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ maxWidth: 800 }}>

        {/* Banner de edição */}
        {editId && !carregandoEdicao && !erroEdicao && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '0.5rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#1e40af', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Editando nota Nº {editId}</span>
            <span style={{ color: '#6b7280' }}>— corrija o que for necessário e retransmita</span>
          </div>
        )}
        {carregandoEdicao && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontSize: '0.9rem' }}>Carregando dados da nota...</div>
        )}
        {erroEdicao && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '0.5rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#dc2626' }}>{erroEdicao}</div>
        )}

        {/* ── Etapa 0: Tomador ──────────────────────────────────────────────── */}
        {!resultado && etapa === 0 && (
          <div>
            <h3 className="etapa-titulo">Selecionar Tomador</h3>
            {!cliente && (
              <div className="busca-cliente-wrap">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Nome ou CNPJ/CPF</label>
                  <div className="busca-cliente-row">
                    <input type="text" placeholder="Digite para buscar..." value={busca}
                      onChange={e => handleBusca(e.target.value)} autoFocus />
                    {buscando && <span className="busca-spinner">Buscando...</span>}
                    <button className="btn btn-primary btn-sm" onClick={() => setModalNovo(true)} style={{ whiteSpace: 'nowrap' }}>+ Novo</button>
                  </div>
                </div>
              </div>
            )}
            {modalNovo && <NovoClienteModal onClose={() => setModalNovo(false)} onSalvo={c => { setModalNovo(false); selecionarCliente(c) }} />}
            {clientes.length > 0 && !cliente && (
              <div className="lista-clientes">
                {clientes.map(c => (
                  <button key={c.cli_cod} className="item-cliente" onClick={() => selecionarCliente(c)}>
                    <div className="item-cliente-linha1"><strong>{c.cli_nom}</strong><span className="badge-tipo">{c.cli_fj === 'J' ? 'PJ' : 'PF'}</span></div>
                    {c.cli_fan && c.cli_fan !== c.cli_nom && <span className="item-cliente-fantasia">{c.cli_fan}</span>}
                    <div className="item-cliente-doc"><span>{c.cli_fj === 'J' ? 'CNPJ' : 'CPF'}: {fmtDoc(c.cli_mat)}</span></div>
                    {(c.end_logradouro?.trim() || c.end_cidade?.trim()) && (
                      <span className="item-cliente-endereco">
                        {[
                          [c.end_logradouro, c.end_numero].filter(Boolean).join(', '),
                          c.end_bairro,
                          c.end_cidade && c.end_uf ? `${c.end_cidade}/${c.end_uf}` : (c.end_cidade || c.end_uf),
                        ].filter(Boolean).join(' — ')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {cliente && (
              <div className="cliente-card">
                <div className="cliente-card-header">
                  <div>
                    <div className="cliente-card-nome">{cliente.cli_nom}</div>
                    {cliente.cli_fan && cliente.cli_fan !== cliente.cli_nom && <div className="cliente-card-fantasia">{cliente.cli_fan}</div>}
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => { setCliente(null); setEndereco(null); setClifroNat(undefined) }}>Trocar</button>
                </div>
                <div className="cliente-card-fiscal">
                  <span><b>{cliente.cli_fj === 'J' ? 'CNPJ' : 'CPF'}:</b> {fmtDoc(cliente.cli_mat)}</span>
                </div>
                {cliente.bloqueado && (
                  <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, margin: '0.5rem 0' }}>
                    ⚠ CLIENTE BLOQUEADO — não é possível emitir documentos para este cliente.
                  </div>
                )}
              </div>
            )}
            {cliente && <div className="etapa-acoes"><button className="btn btn-primary" disabled={cliente.bloqueado} onClick={() => setEtapa(1)}>Próximo →</button></div>}
          </div>
        )}

        {/* ── Etapa 1: Serviço + Impostos ────────────────────────────────── */}
        {!resultado && etapa === 1 && (
          <div>
            <h3 className="etapa-titulo">Serviço e Impostos</h3>

            {/* Barra azul — identificação */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end', background: '#1a6ea8', borderRadius: '0.5rem', padding: '0.65rem 1rem', marginBottom: '1rem' }}>
              <div className="field" style={{ flex: '0 0 220px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Empresa Emitente</label>
                <select
                  value={empresa?.empcod ?? ''}
                  onChange={e => setEmpresa(empresas.find(x => x.empcod === Number(e.target.value)) ?? null)}
                  disabled={editId != null}
                  style={{ fontWeight: 600 }}
                >
                  {empresas.map(emp => (
                    <option key={emp.empcod} value={emp.empcod}>
                      {emp.empcod} - {emp.empfan || emp.empnom}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: '0 0 160px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Competência</label>
                <input type="date" value={dCompet} onChange={e => setDCompet(e.target.value)} />
              </div>
              <div className="field" style={{ flex: '0 0 110px', marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Nº da Nota</label>
                <input type="number" min="1" value={numero} onChange={e => setNumero(e.target.value)} />
              </div>
              <div className="field" style={{ flex: '0 0 170px', marginBottom: 0 }}>
                <label style={{ color: '#fde68a', fontWeight: 700 }}>Nº da Viagem</label>
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Opcional"
                    value={numeroViagem}
                    onChange={e => setNumeroViagem(e.target.value.replace(/\D/g, ''))}
                    style={{ fontWeight: 700, background: '#fffbeb', border: '2px solid #f59e0b', color: '#92400e' }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    title="Buscar fichas de viagem do cliente"
                    disabled={!cliente}
                    onClick={() => setModalFichaViagem(true)}
                    style={{ borderColor: '#f59e0b' }}
                  >🔍</button>
                </div>
              </div>
              {modalFichaViagem && cliente && (
                <FichaViagemModal
                  cnpj={cliente.cli_mat}
                  onClose={() => setModalFichaViagem(false)}
                  onSelect={ficcod => { setNumeroViagem(String(ficcod)); setModalFichaViagem(false) }}
                />
              )}
              <div className="field" style={{ flex: '1 1 240px', minWidth: 0, marginBottom: 0 }}>
                <label style={{ color: '#bfdbfe', fontWeight: 700 }}>Tomador</label>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', background: 'white', borderRadius: '0.45rem', padding: '0.5rem 0.85rem', fontSize: '0.875rem', fontWeight: 600 }}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cliente?.cli_nom} · {fmtDoc(cliente?.cli_mat ?? '')}</span>
                  <button className="btn btn-outline btn-sm" onClick={() => setEtapa(0)} style={{ fontSize: '0.72rem', padding: '2px 10px', flexShrink: 0, whiteSpace: 'nowrap' }}>Trocar</button>
                </div>
              </div>
            </div>

            {/* Endereço tomador */}
            <div style={secStyle}>
              <div style={secLabel}>Endereço do Tomador</div>
              <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
                <div className="field" style={{ flex: '0 0 110px', marginBottom: 0 }}>
                  <label>CEP</label>
                  <input type="text" maxLength={8} value={endTomCEP} onChange={e => setEndTomCEP(e.target.value.replace(/\D/g,''))} placeholder="00000000" />
                </div>
                <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}>
                  <label>Logradouro</label>
                  <input type="text" value={endTomLgr} onChange={e => setEndTomLgr(e.target.value)} placeholder="Rua / Av." />
                </div>
                <div className="field" style={{ flex: '0 0 80px', marginBottom: 0 }}>
                  <label>Número</label>
                  <input type="text" value={endTomNro} onChange={e => setEndTomNro(e.target.value)} placeholder="SN" />
                </div>
                <div className="field" style={{ flex: '0 0 120px', marginBottom: 0 }}>
                  <label>Complemento</label>
                  <input type="text" value={endTomCpl} onChange={e => setEndTomCpl(e.target.value)} />
                </div>
                <div className="field" style={{ flex: '1 1 140px', marginBottom: 0 }}>
                  <label>Bairro</label>
                  <input type="text" value={endTomBairro} onChange={e => setEndTomBairro(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Itens já adicionados à nota */}
            {itens.length > 0 && (
              <div style={{ ...secStyle, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                <div style={{ ...secLabel, color: '#15803d' }}>Itens da nota ({itens.length})</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <tbody>
                    {itens.map((it, i) => (
                      <tr key={i} style={{ background: itemEditIdx === i ? '#dcfce7' : undefined, borderBottom: '1px solid #d1fae5' }}>
                        <td style={{ width: 28, padding: '5px 4px', fontWeight: 700, color: '#6b7280' }}>{i + 1}</td>
                        <td style={{ padding: '5px 6px' }}>
                          {it.xServ.length > 70 ? `${it.xServ.slice(0, 70)}…` : it.xServ}
                          <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>Cód. {it.cServ}{it.cNBS ? ` · NBS ${it.cNBS}` : ''}{it.itsDes ? ` · ${it.itsDes}` : ''}</div>
                        </td>
                        <td style={{ padding: '5px 6px', textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{brl(n(it.vServ))}</td>
                        <td style={{ padding: '5px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => editarItem(i)}>Editar</button>{' '}
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => removerItem(i)}>Remover</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} style={{ padding: '6px', textAlign: 'right', fontWeight: 700 }}>Total</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontWeight: 800, color: '#15803d', fontVariantNumeric: 'tabular-nums' }}>{brl(totServ)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Dados do serviço */}
            <div style={secStyle}>
              <div style={secLabel}>
                {itemEditIdx !== null ? `Editando o item ${itemEditIdx + 1}` : itens.length > 0 ? (formPreenchido ? `Novo item (${itens.length + 1})` : 'Adicionar outro item (opcional — deixe em branco para seguir só com os itens acima)') : 'Dados do Serviço'}
              </div>
              {/* CFOP */}
              <div className="field" style={{ marginBottom: '0.65rem' }}>
                <label>CFOP / Natureza da Operação *</label>
                <NaturezaSelect naturezas={naturezasInfisc} value={natureza} onChange={v => setNatureza(v)} />
              </div>
              <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginBottom: '0.65rem' }}>
                <div className="field" style={{ flex: '0 0 260px', marginBottom: 0 }}>
                  <label>Local de Prestação (IBGE) *</label>
                  <div style={cfopDivergente ? { outline: '2px solid #dc2626', borderRadius: '0.375rem' } : undefined}>
                    <CidadeSelect
                      codValue={cLocCod} desValue={cLocXMun} ibgeValue={cLocPrest} ufValue={cLocUf}
                      filtroUf="RS"
                      onChange={(cod, des, ibge, uf) => { setCLocCod(cod); setCLocXMun(des); setCLocPrest(ibge); setCLocUf(uf) }}
                    />
                  </div>
                  {cfopDivergente && (
                    <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      Tem que ser {natMunBase?.cidade_des} — cidade cadastrada na CFOP selecionada
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', marginBottom: '0.65rem' }}>
                <div className="field" style={{ flex: '1 1 260px', marginBottom: 0 }}>
                  <label>Item *</label>
                  <ItemSelect somenteAtivos
                    codValue={itsCod}
                    descValue={itsDes}
                    unValue={itsUn}
                    onChange={(cod, des, un, precod, _ncm, _cest, forcod, _esp, srvcod) => {
                      setItsCod(cod)
                      setItsDes(des)
                      setItsUn(un)
                      if (forcod) setCServ(forcod)
                      if (precod) setCNBS(precod)
                      if (srvcod) setCLCServ(srvcod)
                      // Troca a descrição se estiver vazia ou ainda for a do item anterior (sem texto digitado à mão)
                      if (des && (!xServ.trim() || xServ.trim() === itsDes.trim())) setXServ(des)
                      if (un) setUTrib(un)
                    }}
                  />
                </div>
                <div className="field" style={{ flex: '0 0 140px', marginBottom: 0 }}>
                  <label>Cód. Serviço *</label>
                  <input type="text" value={cServ} onChange={e => setCServ(e.target.value)} placeholder="" />
                </div>
                <div className="field" style={{ flex: '0 0 130px', marginBottom: 0 }}>
                  <label>NBS (opcional)</label>
                  <input type="text" value={cNBS} onChange={e => setCNBS(e.target.value)} placeholder="" />
                </div>
              </div>
              <div className="field" style={{ marginBottom: '0.65rem' }}>
                <label>Descrição do Serviço *</label>
                <textarea rows={3} value={xServ} onChange={e => setXServ(e.target.value)} placeholder="Descreva o serviço prestado..." style={{ resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
                <div className="field" style={{ flex: '0 0 80px', marginBottom: 0 }}>
                  <label>Unid.</label>
                  <input type="text" value={uTrib} onChange={e => setUTrib(e.target.value)} placeholder="UN" />
                </div>
                <div className="field" style={{ flex: '0 0 100px', marginBottom: 0 }}>
                  <label>Qtd.</label>
                  <input type="text" value={qTrib} onChange={e => setQTrib(e.target.value)} placeholder="1.00" />
                </div>
                <div className="field" style={{ flex: '1 1 130px', marginBottom: 0 }}>
                  <label>Valor Unitário (R$)</label>
                  <input type="text" value={vUnit} onChange={e => setVUnit(e.target.value)} placeholder="0,00" />
                </div>
                <div className="field" style={{ flex: '1 1 150px', marginBottom: 0 }}>
                  <label>Valor do Serviço (R$) *</label>
                  <input type="text" value={vServ} onChange={e => setVServ(e.target.value)} placeholder="0,00" />
                </div>
              </div>
            </div>

            {/* IBS/CBS Regra */}
            <div className="field" style={{ marginBottom: '0.65rem' }}>
              <label>IBS/CBS (Regra Reforma Tributária)</label>
              <div style={ibsCbsDivergente ? { outline: '2px solid #dc2626', borderRadius: '0.375rem' } : undefined}>
                <IbsCbsSelect codValue={ibsCbsRegra} desValue={ibsCbsDes} docFiscalPermitido="99"
                  onChange={(cod, des) => { setIbsCbsRegra(cod); setIbsCbsDes(des) }} />
              </div>
              {ibsCbsDivergente && (
                <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                  Regra vinculada a cidade diferente do Local de Prestação informado
                </div>
              )}
            </div>

            {/* Abas fiscais */}
            <div style={{ ...secStyle, padding: 0, overflow: 'hidden' }}>
              <div className="fiscal-tabs" style={{ borderBottom: '1px solid #e5e7eb', padding: '0 0.5rem' }}>
                <button type="button"
                  className={`fiscal-tab${abaFiscal === 'impostos' ? ' fiscal-tab--ativa' : ''}`}
                  onClick={() => setAbaFiscal('impostos')}>Impostos</button>
                <button type="button"
                  className={`fiscal-tab${abaFiscal === 'ibscbs' ? ' fiscal-tab--ativa' : ''}`}
                  onClick={() => setAbaFiscal('ibscbs')}>IBS/CBS/IS</button>
              </div>

              {abaFiscal === 'impostos' && (
                <div style={{ padding: '0.65rem 0.75rem' }}>
                  <table className="imp-table">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>Tributo</th>
                        <th style={{ width: 130 }}>Base (R$)</th>
                        <th style={{ width: 90 }}>Alíquota (%)</th>
                        <th style={{ width: 110 }}>Destaque</th>
                        <th style={{ width: 110, background: '#c2410c', color: '#fff' }}>Retenção</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'ISS',    bc: vBCISS,      setBC: setVBCISS,      p: pISS,         setP: setPISS,         v: vISS,    retido: issRetido,  isIss: true  },
                        { label: 'INSS',   bc: vBCINSS,     setBC: setVBCINSS,     p: pRetINSS,     setP: setPRetINSS,     v: vINSS,   retido: inssRetido, isIss: false },
                        { label: 'IR',     bc: vBCRetIR,    setBC: setVBCRetIR,    p: pRetIR,       setP: setPRetIR,       v: vIR,     retido: irRetido,   isIss: false },
                        { label: 'PIS',    bc: vBCPISPASEP, setBC: setVBCPISPASEP, p: pRetPISPASEP, setP: setPRetPISPASEP, v: vPIS,    retido: pisRetido,  isIss: false },
                        { label: 'COFINS', bc: vBCCOFINS,   setBC: setVBCCOFINS,   p: pRetCOFINS,   setP: setPRetCOFINS,   v: vCOFINS, retido: false,      isIss: false },
                        { label: 'CSLL',   bc: vBCCSLL,     setBC: setVBCCSLL,     p: pRetCSLL,     setP: setPRetCSLL,     v: vCSLL,   retido: false,      isIss: false },
                      ].map(row => (
                        <tr key={row.label}>
                          <td className="imp-label">{row.label}</td>
                          <td><input type="text" className="imp-input" value={row.bc}
                            onChange={e => row.setBC(e.target.value)} placeholder={n(vServ).toFixed(2)} /></td>
                          <td><input type="text" className="imp-input imp-aliq" value={row.p}
                            onChange={e => row.setP(e.target.value)} /></td>
                          <td>
                            {n(row.v) > 0
                              ? <input type="text" className="imp-input" readOnly value={row.v}
                                  style={{ fontWeight: 800, color: row.isIss ? '#065f46' : '#374151' }} />
                              : <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af' }}>—</span>
                            }
                          </td>
                          <td>
                            {row.retido && n(row.v) > 0
                              ? <input type="text" className="imp-input" readOnly value={row.v}
                                  style={{ fontWeight: 600, color: '#c2410c', borderColor: '#fb923c' }} />
                              : <span style={{ display: 'block', textAlign: 'center', color: '#9ca3af' }}>—</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {abaFiscal === 'ibscbs' && (
                <div style={{ padding: '0.65rem 0.75rem' }}>
                  {!ibsCbsRegra && (
                    <div style={{ color: '#9ca3af', fontSize: '0.82rem', padding: '0.5rem 0' }}>
                      Selecione a regra IBS/CBS acima para calcular automaticamente.
                    </div>
                  )}
                  {ibsCbsRegra && (
                    <table className="imp-table">
                      <thead>
                        <tr>
                          <th style={{ width: 70 }}>Tributo</th>
                          <th style={{ width: 55 }}>CST</th>
                          <th style={{ width: 110 }}>Base</th>
                          <th style={{ width: 110 }}>BC Efetiva</th>
                          <th style={{ width: 75 }}>Ali. Ref %</th>
                          <th style={{ width: 75 }}>Red. Ali %</th>
                          <th style={{ width: 75 }}>Ali. Efet %</th>
                          <th style={{ width: 110 }}>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { label: 'CBS',     cst: ibsCbs.cst,      bc: ibsCbs.cbsBc,   bcEf: ibsCbs.cbsBcEf,   aliRef: ibsCbs.cbsAliRef,   red: ibsCbs.cbsRedAli,   aliEf: ibsCbs.cbsAliEf,   val: ibsCbs.cbsValor   },
                          { label: 'IBS UF',  cst: ibsCbs.ibsUfEst, bc: ibsCbs.cbsBc,   bcEf: ibsCbs.ibsUfBcEf, aliRef: ibsCbs.ibsUfAliRef, red: ibsCbs.ibsUfRedAli, aliEf: ibsCbs.ibsUfAliEf, val: ibsCbs.ibsUfValor },
                          { label: 'IBS MUN', cst: '',              bc: ibsCbs.cbsBc,   bcEf: ibsCbs.ibsMunBcEf,aliRef: ibsCbs.ibsMunAliRef,red: ibsCbs.ibsMunRedAli,aliEf: ibsCbs.ibsMunAliEf,val: ibsCbs.ibsMunValor},
                          { label: 'IS',      cst: '',              bc: ibsCbs.isSbas,  bcEf: ibsCbs.isSbas,    aliRef: ibsCbs.isSali,      red: 0,                  aliEf: ibsCbs.isSali,     val: r2(ibsCbs.isSbas * ibsCbs.isSali / 100) },
                        ].map(row => (
                          <tr key={row.label}>
                            <td className="imp-label">{row.label}</td>
                            <td style={{ textAlign: 'center', fontSize: '0.78rem', color: '#6b7280' }}>{row.cst}</td>
                            <td><input className="imp-input" type="text" readOnly value={(row.bc || 0).toFixed(2)} /></td>
                            <td><input className="imp-input" type="text" readOnly value={(row.bcEf || 0).toFixed(2)} /></td>
                            <td><input className="imp-input" type="text" readOnly value={(row.aliRef || 0).toFixed(2)} /></td>
                            <td><input className="imp-input" type="text" readOnly value={(row.red || 0).toFixed(2)} /></td>
                            <td><input className="imp-input" type="text" readOnly value={(row.aliEf || 0).toFixed(2)} /></td>
                            <td><input className="imp-input" type="text" readOnly value={(row.val || 0).toFixed(2)} style={{ fontWeight: 800, color: '#1a6ea8' }} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
              {(itemEditIdx !== null || formPreenchido) && (
                <button type="button" className="btn btn-outline btn-sm" onClick={cancelarEdicaoItem}>
                  {itemEditIdx !== null ? 'Cancelar edição' : 'Limpar item'}
                </button>
              )}
              <button type="button" className="btn btn-secondary btn-sm" onClick={adicionarItem}>
                {itemEditIdx !== null ? `✓ Salvar item ${itemEditIdx + 1}` : '＋ Adicionar item à nota'}
              </button>
            </div>

            {/* Informações adicionais */}
            <div className="field">
              <label>Informações Adicionais</label>
              <textarea
                value={infAdics}
                onChange={e => setInfAdics(e.target.value)}
                placeholder="Observações, instruções de cobrança, dados do contrato..."
                rows={3}
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.85rem' }}
              />
            </div>

            {erroEtapa1 && (
              <div style={{ color: '#dc2626', fontSize: '0.82rem', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 4, padding: '6px 10px', marginBottom: '0.5rem' }}>
                {erroEtapa1}
              </div>
            )}
            <div className="etapa-acoes">
              <button className="btn btn-outline" onClick={() => setEtapa(0)}>← Voltar</button>
              <button className="btn btn-primary" onClick={avancarRevisao}>Revisar →</button>
            </div>
          </div>
        )}

        {/* ── Etapa 2: Revisão ──────────────────────────────────────────────── */}
        {!resultado && etapa === 2 && (() => {
          const secR: React.CSSProperties = { marginBottom: '1rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '0.75rem 1rem' }
          const labelR: React.CSSProperties = { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '0.4rem' }
          const rowR: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.18rem 0', borderBottom: '1px dashed #e5e7eb' }

          const natObj = naturezas.find(nat => String(nat.tno_cod) === natureza)

          return (
            <div>
              <h3 className="etapa-titulo">Revisão e Emissão — NFS-e INFISC</h3>

              {/* Cabeçalho: tomador + documento + total */}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                <div style={{ ...secR, flex: '1 1 220px' }}>
                  <div style={labelR}>Tomador</div>
                  <div style={{ fontWeight: 700 }}>{cliente?.cli_nom}</div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{fmtDoc(cliente?.cli_mat ?? '')}</div>
                </div>
                <div style={{ ...secR, flex: '1 1 180px' }}>
                  <div style={labelR}>Documento</div>
                  <div style={{ fontWeight: 700 }}>NFS-e INFISC · Série {serie} · Nº {numero}</div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Competência: {dCompet}</div>
                </div>
                <div style={{ ...secR, flex: '1 1 180px' }}>
                  <div style={labelR}>Local da Prestação do Transporte</div>
                  <div style={{ fontWeight: 700 }}>{cLocXMun} / {cLocUf}</div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>IBGE {cLocPrest}</div>
                </div>
                <div style={{ ...secR, flex: '0 0 auto', minWidth: 160 }}>
                  <div style={labelR}>Total</div>
                  <div style={{ fontWeight: 800, fontSize: '1.3rem', color: '#f07820', fontVariantNumeric: 'tabular-nums' }}>{brl(totServ)}</div>
                  {totalRetencoes > 0 && <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: 4 }}>(-) Retenções: {brl(totalRetencoes)}</div>}
                  {totalRetencoes > 0 && <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#15803d', marginTop: 4, borderTop: '1px solid #e5e7eb', paddingTop: 4 }}>Líquido: {brl(vLiq)}</div>}
                  {finParcelas.length > 0 && (
                    <div style={{ fontSize: '0.78rem', color: '#065f46', marginTop: 4 }}>
                      {finParcelas.length}× parcela{finParcelas.length > 1 ? 's' : ''} · {finContaNome || finContaCod}
                    </div>
                  )}
                </div>
              </div>

              {/* CFOP */}
              {natObj && (
                <div style={secR}>
                  <div style={labelR}>CFOP</div>
                  <div style={rowR}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>{natObj.tno_cdf} — {natObj.tno_des}</span>
                  </div>
                </div>
              )}

              {/* Um bloco Serviço + Tributos por item */}
              {itens.map((it, idx) => {
                const retItem = retencaoItem(it)
                const ibsTotalItem = it.ibsCbs.cbsValor + it.ibsCbs.ibsUfValor + it.ibsCbs.ibsMunValor + r2(it.ibsCbs.isSbas * it.ibsCbs.isSali / 100)
                return (
                  <React.Fragment key={idx}>
                    <div style={secR}>
                      <div style={labelR}>{itens.length > 1 ? `Serviço ${idx + 1} de ${itens.length}` : 'Serviço'}</div>
                      <div style={rowR}><span style={{ fontWeight: 600 }}>{it.xServ}</span></div>
                      <div style={rowR}>
                        <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>Cód. Serviço: {it.cServ}{it.cLCServ ? ` · LC 116: ${it.cLCServ}` : ''}{it.cNBS ? ` · NBS: ${it.cNBS}` : ''}</span>
                        <span style={{ fontSize: '0.78rem' }}>{n(it.qTrib).toFixed(2)} {it.uTrib} × {brl(n(it.vUnit || it.vServ))}</span>
                      </div>
                      {itens.length > 1 && (
                        <div style={rowR}>
                          <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>Valor do serviço</span>
                          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{brl(n(it.vServ))}</span>
                        </div>
                      )}
                    </div>

                    <div style={secR}>
                      <div style={labelR}>{itens.length > 1 ? `Tributos — item ${idx + 1}` : 'Tributos'}</div>
                      {[
                        { nome: it.issRetido ? 'ISS Retido' : 'ISS',       valor: n(it.vISS),    aliq: it.pISS,          show: n(it.vISS) > 0,    cor: '#dc2626', retido: it.issRetido },
                        { nome: 'INSS Retido',                              valor: n(it.vINSS),   aliq: it.pRetINSS,      show: n(it.vINSS) > 0,   cor: '#dc2626', retido: true  },
                        { nome: 'IRRF',                                     valor: n(it.vIR),     aliq: it.pRetIR,        show: n(it.vIR) > 0,     cor: '#dc2626', retido: true  },
                        { nome: it.pisRetido ? 'PIS Retido' : 'PIS',       valor: n(it.vPIS),    aliq: it.pRetPISPASEP,  show: n(it.vPIS) > 0,    cor: '#dc2626', retido: it.pisRetido },
                        { nome: it.pisRetido ? 'COFINS Retido' : 'COFINS', valor: n(it.vCOFINS), aliq: it.pRetCOFINS,    show: n(it.vCOFINS) > 0, cor: '#dc2626', retido: it.pisRetido },
                        { nome: 'CSLL Retido',                              valor: n(it.vCSLL),   aliq: it.pRetCSLL,      show: n(it.vCSLL) > 0,   cor: '#dc2626', retido: true  },
                      ].filter(t => t.show).map((t, i) => (
                        <div key={i} style={rowR}>
                          <span style={{ color: t.retido ? '#dc2626' : '#6b7280', fontWeight: t.retido ? 700 : 400 }}>{t.nome} ({t.aliq}%)</span>
                          <span style={{ fontWeight: 600, color: t.cor, fontVariantNumeric: 'tabular-nums' }}>{brl(t.valor)}</span>
                        </div>
                      ))}
                      {ibsTotalItem > 0 && (
                        <div style={rowR}>
                          <span style={{ color: '#1a6ea8' }}>IBS/CBS/IS ({it.ibsCbsDes})</span>
                          <span style={{ fontWeight: 600, color: '#1a6ea8', fontVariantNumeric: 'tabular-nums' }}>{brl(ibsTotalItem)}</span>
                        </div>
                      )}
                      {(n(it.vISS) + retItem + ibsTotalItem) === 0 && (
                        <div style={{ fontSize: '0.78rem', color: '#9ca3af', padding: '2px 0' }}>Sem tributos calculados</div>
                      )}
                      {itens.length === 1 && retItem > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '0.82rem', paddingTop: '0.35rem', borderTop: '1px solid #e5e7eb', marginTop: '0.25rem' }}>
                          <span>Total retenções</span>
                          <span style={{ color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{brl(retItem)}</span>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                )
              })}
              {itens.length > 1 && totalRetencoes > 0 && (
                <div style={{ ...secR, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '0.85rem' }}>
                  <span>Total das retenções da nota</span>
                  <span style={{ color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{brl(totalRetencoes)}</span>
                </div>
              )}

              {infAdics.trim() && (
                <div style={secR}>
                  <div style={labelR}>Informações Adicionais</div>
                  <div style={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'pre-wrap' }}>{infAdics.trim()}</div>
                </div>
              )}

              <div className="etapa-acoes">
                <button className="btn btn-outline" onClick={() => setEtapa(1)}>← Voltar</button>
                <button type="button"
                  onClick={() => {
                    const vTotal = totServ
                    const base = new Date(dCompet)
                    setFinParcelas(Array.from({ length: finQtdParcelas }, (_, i) => {
                      const d = new Date(base)
                      d.setDate(d.getDate() + finIntervalo * (i + 1))
                      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (vTotal / finQtdParcelas).toFixed(2) }
                    }))
                    setFinanceiroModal(true)
                  }}
                  style={{ background: '#065f46', color: '#fff', border: 'none', borderRadius: 6, padding: '0.4rem 1rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
                  {finParcelas.length > 0 && finContaCod ? 'Financeiro ✓' : '$ Financeiro'}
                </button>
                <button className="btn btn-primary" disabled={enviando || cliente?.bloqueado} onClick={emitir}>
                  {enviando ? 'Emitindo...' : 'Emitir NFS-e INFISC'}
                </button>
              </div>
            </div>
          )
        })()}

        {/* ── Resultado ─────────────────────────────────────────────────────── */}
        {resultado && (
          <div>
            {resultado.ok ? (
              <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#15803d', marginBottom: 12 }}>NFS-e INFISC emitida!</div>
                {resultado.nNFSe && <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: 8 }}>NFS-e Nº {resultado.nNFSe} · Série {resultado.serie || serie} · Sistema Nº {resultado.numero}</div>}
                {resultado.chaveAcesso && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '10px 16px', fontSize: '0.82rem', fontFamily: 'monospace', wordBreak: 'break-all', margin: '0 auto', maxWidth: 600, color: '#166534' }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Chave de Acesso</div>
                    {resultado.chaveAcesso}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>❌</div>
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#b91c1c', marginBottom: 12 }}>Erro na emissão</div>
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 16px', fontSize: '0.88rem', color: '#991b1b', margin: '0 auto', maxWidth: 600, whiteSpace: 'pre-wrap' }}>
                  {resultado.xMotivo || resultado.erro || 'A prefeitura não informou o motivo — consulte a nota na Lista / Transmissão.'}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.2rem', gap: 10 }}>
              {/* Com erro a nota já está gravada com esse número — só deixa corrigir e reenviar a mesma */}
              {resultado.ok ? (
                <button className="btn btn-primary" onClick={nova}>Nova NFS-e</button>
              ) : (
                <button className="btn btn-primary" onClick={() => {
                  if (editId == null && resultado.numero) setEditId(Number(resultado.numero))
                  setResultado(null); setEtapa(1)
                }}>Corrigir e Reenviar</button>
              )}
              {/* Nota já gravada (tem número): dá pra deixar rejeitada na Lista e corrigir depois */}
              {!resultado.ok && resultado.numero && (
                <button className="btn btn-outline" onClick={() => navigate('/nfse-infisc/lista')}>Deixar rejeitada e ir para a Lista</button>
              )}
            </div>
          </div>
        )}
      </div>

    {/* ── Modal Financeiro ── */}
    {financeiroModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '24px 12px' }}
        onClick={() => setFinanceiroModal(false)}>
        <div style={{ background: '#fff', borderRadius: 8, width: 560, maxWidth: '96vw', maxHeight: 'calc(100vh - 48px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ background: '#065f46', color: '#fff', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>Financeiro — NFS-e INFISC</div>
              <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                Série {serie} · Nº {numero} — Total: {totServ.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </div>
              {finCondDes && (
                <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: 2 }}>
                  Condição de Pagamento: {finCondDes}
                </div>
              )}
            </div>
            <button onClick={() => setFinanceiroModal(false)} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', cursor: 'pointer', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '1rem' }}>✕</button>
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Qtd. Parcelas</label>
                <input type="number" min={1} max={36} value={finQtdParcelas}
                  onChange={e => {
                    const qtd = Math.max(1, Math.min(36, Number(e.target.value)))
                    setFinQtdParcelas(qtd)
                    const base = new Date(dCompet)
                    const vTotal = totServ
                    setFinParcelas(Array.from({ length: qtd }, (_, i) => {
                      const d = new Date(base); d.setDate(d.getDate() + finIntervalo * (i + 1))
                      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (vTotal / qtd).toFixed(2) }
                    }))
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Intervalo (dias)</label>
                <input type="number" min={0} value={finIntervalo}
                  onChange={e => {
                    const intervalo = Math.max(0, Number(e.target.value))
                    setFinIntervalo(intervalo)
                    const base = new Date(dCompet)
                    const vTotal = totServ
                    setFinParcelas(Array.from({ length: finQtdParcelas }, (_, i) => {
                      const d = new Date(base); d.setDate(d.getDate() + intervalo * (i + 1))
                      return { numero: String(i + 1).padStart(2, '0'), vencimento: d.toISOString().slice(0, 10), valor: (vTotal / finQtdParcelas).toFixed(2) }
                    }))
                  }}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 }}>Conta Financeira *</label>
              <div style={{ display: 'flex', gap: '0.5rem', position: 'relative' }}>
                <input type="text" value={finContaCod}
                  onChange={e => { setFinContaCod(e.target.value); setFinContaNome('') }}
                  onBlur={async e => {
                    const cod = e.target.value.trim(); if (!cod) return
                    const r = await api.get('/erp/contas', { params: { busca: cod } })
                    const found = r.data.find((c: any) => c.dep_cod.trim() === cod)
                    if (found) setFinContaNome(found.dep_des)
                  }}
                  placeholder="Código" autoComplete="off"
                  style={{ width: 80, border: `1px solid ${finContaCod.trim() ? '#d1d5db' : '#fca5a5'}`, borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
                <div style={{ flex: 1, position: 'relative' }}>
                  {finContaCod && finContaNome ? (
                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #6ee7b7', borderRadius: 4, padding: '6px 8px', background: '#f0fdf4', fontSize: '0.85rem' }}>
                      <span style={{ flex: 1, color: '#065f46', fontWeight: 600 }}>{finContaNome}</span>
                      <button onClick={() => { setFinContaCod(''); setFinContaNome('') }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0 2px', fontSize: '1rem', lineHeight: 1 }}>×</button>
                    </div>
                  ) : (
                    <>
                      <input type="text" value={finContaNome}
                        onChange={async e => {
                          const v = e.target.value; setFinContaNome(v); setFinContaCod('')
                          if (v.length >= 2) {
                            const r = await api.get('/erp/contas', { params: { busca: v } })
                            setFinContaResultados(r.data); setFinContaAberto(true)
                          } else { setFinContaResultados([]); setFinContaAberto(false) }
                        }}
                        onBlur={() => setTimeout(() => setFinContaAberto(false), 150)}
                        placeholder="Digite para buscar..." autoComplete="off"
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px', fontSize: '0.85rem' }} />
                      {finContaAberto && finContaResultados.length > 0 && (
                        <div style={{ position: 'absolute', zIndex: 50, background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: '100%', maxHeight: 200, overflowY: 'auto', top: '100%', left: 0 }}>
                          {finContaResultados.map(c => (
                            <div key={c.dep_cod} onMouseDown={() => { setFinContaCod(c.dep_cod.trim()); setFinContaNome(c.dep_des); setFinContaAberto(false) }}
                              style={{ padding: '7px 10px', cursor: 'pointer', fontSize: '0.82rem', borderBottom: '1px solid #f3f4f6' }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f0fdf4')}
                              onMouseLeave={e => (e.currentTarget.style.background = '')}>
                              <span style={{ fontFamily: 'monospace', fontWeight: 700, marginRight: 8, color: '#374151' }}>{c.dep_cod.trim()}</span>
                              <span style={{ color: '#6b7280' }}>{c.dep_des}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {finParcelas.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr className="thead-claro" style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'center', width: 60 }}>Parcela</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Vencimento</th>
                    <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Valor (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {finParcelas.map((p, i) => (
                    <tr key={i}>
                      <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'center', fontWeight: 700, color: '#6b7280' }}>{p.numero}</td>
                      <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>
                        <input type="date" value={p.vencimento}
                          onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, vencimento: e.target.value } : x))}
                          style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px 8px', border: '1px solid #e5e7eb' }}>
                        <input type="number" value={p.valor}
                          onChange={e => setFinParcelas(prev => prev.map((x, j) => j === i ? { ...x, valor: e.target.value } : x))}
                          style={{ border: 'none', background: 'transparent', fontSize: '0.82rem', textAlign: 'right', width: '100%' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="thead-claro" style={{ background: '#f9fafb', fontWeight: 700 }}>
                    <td colSpan={2} style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Total</td>
                    <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', color: '#065f46' }}>
                      {finParcelas.reduce((s, p) => s + Number(p.valor), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.65rem 1.25rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', background: '#f9fafb' }}>
            <button onClick={() => { setFinParcelas([]); setFinContaCod(''); setFinContaNome(''); setFinanceiroModal(false) }}
              style={{ padding: '5px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.82rem', cursor: 'pointer', color: '#6b7280' }}>Limpar</button>
            <button onClick={() => setFinanceiroModal(false)}
              disabled={finParcelas.length > 0 && !finContaCod.trim()}
              style={{ padding: '5px 18px', background: '#065f46', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.82rem', fontWeight: 600, cursor: finParcelas.length > 0 && !finContaCod.trim() ? 'not-allowed' : 'pointer', opacity: finParcelas.length > 0 && !finContaCod.trim() ? 0.6 : 1 }}>
              Confirmar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  )
}
